import React, { useCallback, useEffect, useMemo } from "react";
import { useFrame } from "./FrameContext";
import { useModel } from "./ModelContext";
import { useMedia } from "./MediaContext";
import {
    useMediaStore,
    useCurrentMedia,
    useCurrentMediaFileInferredDimensions,
    useCurrentMediaFrameInferredDimensions,
    useUserAnnotationsSnapshot,
    useFlattenedVideoSourceId
} from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { ComputedValues, ComputedValuesContext } from "./ComputedValuesContext";
import { FrameInferenceResult } from "@/types/inference";
import { omit, values } from "lodash";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@/utils/predictions";
import { useFps } from "@/contexts/FpsContext";
import { MIN_INFERENCES_SAMPLE } from "@/constants/inference";
import type { Annotation } from "@/types/annotation";
import { Prediction } from "../utils/api/inference";
import { getExtractedFrameIdsForMediaItem, useMediaUtils } from "@/hooks/useMediaUtils";
import { MediaItemStatus } from "@/types/media";
import { getFrameAnnotations } from "@/utils/resolveAnnotations";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows } from "@/types/flows";

export const ComputedValuesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { selectedAnnotationId } = useFrame();
    const { annotationsForFileAndFrameId, predictionsForFileAndFrameSegment, promptLabels } =
        useModel();
    const { storageUploadDir, mediaItems } = useMedia();

    const media = useMediaStore();
    const currentMedia = useCurrentMedia();
    const currentMediaFileInferredDimensions = useCurrentMediaFileInferredDimensions();
    const currentMediaFrameInferredDimensions = useCurrentMediaFrameInferredDimensions();

    const { uploadMediaItem } = useMediaUtils();
    const { fps } = useFps();
    const { flow } = useFlowState();

    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceUrl = currentWorkspace?.url ?? "anonymous";
    const userAnnotationsSnapshot = useUserAnnotationsSnapshot();
    const flattenedVideoSourceId = useFlattenedVideoSourceId();

    // Get all annotations from ALL files
    const allAnnotationsAcrossFiles: Annotation[] = useMemo(
        () =>
            values(annotationsForFileAndFrameId).flatMap((annotationsForFile) =>
                values(annotationsForFile).flat()
            ),
        [annotationsForFileAndFrameId]
    );

    const allPredictionsAcrossFiles: Prediction[] = useMemo(
        () =>
            values(predictionsForFileAndFrameSegment).flatMap((predictionsByFrame) =>
                values(predictionsByFrame).flat()
            ),
        [predictionsForFileAndFrameSegment]
    );

    // Generate combined training data from ALL uploaded/uploading files
    // TODO: I loosened the requirement to include uploading files (ex: for videos) MJS
    // might need to tighten this up later
    const currentPromptingData = useMemo(() => {
        const promptingData: any[] = [];

        // all files and/or extracted frames that have been uploaded
        const uploadedMediaItems: typeof mediaItems = [];

        mediaItems.forEach((mediaItem) => {
            const extractedFrameIds = getExtractedFrameIdsForMediaItem(mediaItem);

            if (mediaItem.status === MediaItemStatus.COMPLETED) {
                uploadedMediaItems.push(mediaItem);
            } else if (extractedFrameIds.length > 0) {
                extractedFrameIds.forEach((id) => {
                    const frame = media[id];
                    if (frame?.status === MediaItemStatus.COMPLETED) {
                        uploadedMediaItems.push(frame);
                    }
                });
            }
        });

        uploadedMediaItems.forEach((mediaItem) => {
            const { naturalDimensions, serverMediaDimensions } = mediaItem.metadata || {};

            if (!mediaItem.id) {
                return;
            }

            const dimensions = naturalDimensions || serverMediaDimensions;
            if (!dimensions) {
                return;
            }

            const fileAnnotations = annotationsForFileAndFrameId[mediaItem.id] || {};

            // Process annotations from this file and add to combined training data
            Object.entries(fileAnnotations).forEach(([frameId, frameAnnotations]) => {
                // Type guard to ensure frameAnnotations is an array
                if (!Array.isArray(frameAnnotations)) {
                    return;
                }

                const validAnnotations = frameAnnotations.filter((annotation) => !annotation.draft);

                if (validAnnotations.length === 0) {
                    return;
                }

                const trainingEntry = {
                    file_uuid: `${workspaceUrl}/${frameId}`,
                    width: dimensions.width,
                    height: dimensions.height,
                    boxes: validAnnotations.map((annotation: any) => ({
                        x: Math.round(annotation.x),
                        y: Math.round(annotation.y),
                        w: Math.round(annotation.width),
                        h: Math.round(annotation.height),
                        negative: false,
                        cls: annotation.label
                    }))
                };

                promptingData.push(trainingEntry);
            });
        });

        return promptingData;
    }, [media, mediaItems, annotationsForFileAndFrameId, workspaceUrl]);

    const currentImageInferenceSampledData = useMemo(() => {
        // we need promptLabels in the dependency array because of the confidence threshold (which is on promptLabels)
        // effecting if an inference sample has any annotation we can use to build the RF-DETR model
        // more information here; https://roboflow.slack.com/archives/C08P1KK6N0K/p1758284624289169?thread_ts=1758212203.399899&cid=C08P1KK6N0K
        if (!promptLabels) {
            return [];
        }

        let imageInferenceSampledData: FrameInferenceResult[] = [];
        // We get max MIN_INFERENCES_SAMPLE inference results from images.
        // We try to first get the ones the user has changed the confidence threshold for.
        const imageMediaItems = mediaItems
            .filter((mediaItem) => !mediaItem.isVideo)
            // Sorts by confidence threshold being different from default value -
            // trying to prioritize images that the user has interacted with.
            .sort(
                (a, b) =>
                    Math.abs((a.confidenceThreshold || 0) - DEFAULT_CONFIDENCE_THRESHOLD) -
                    Math.abs((b.confidenceThreshold || 0) - DEFAULT_CONFIDENCE_THRESHOLD)
            )
            .slice(0, MIN_INFERENCES_SAMPLE);

        // Add inference results from images:
        imageMediaItems.forEach((mediaItem) => {
            const predictions = allPredictionsAcrossFiles.filter(
                (p) =>
                    !p.draft &&
                    p.fileId === mediaItem.id &&
                    p.confidence >= (mediaItem.confidenceThreshold || 0)
            );

            const { serverMediaDimensions } = mediaItem.metadata || {};

            if (!serverMediaDimensions) return;

            const frameInferenceResult: FrameInferenceResult = {
                frame_number: mediaItem.metadata?.segment || 0,
                file_uuid: `${workspaceUrl}/${mediaItem.id}`,
                predictions: predictions.map(
                    (p) =>
                        omit(p, [
                            "thumbnailDataUrl",
                            "label",
                            "draft",
                            "fileId",
                            "frameSegment"
                        ]) as FrameInferenceResult["predictions"][number]
                ),
                image_width: serverMediaDimensions.width,
                image_height: serverMediaDimensions.height,
                file_name: mediaItem.name,
                file_type: mediaItem.fileType
            };

            imageInferenceSampledData.push(frameInferenceResult);
        });

        return imageInferenceSampledData;
    }, [promptLabels, mediaItems, allPredictionsAcrossFiles, workspaceUrl]);

    const currentVideoInferenceSampledData = useMemo(() => {
        // we need promptLabels in the dependency array because of the confidence threshold (which is on promptLabels)
        // effecting if an inference sample has any annotation we can use to build the RF-DETR model
        // more information here; https://roboflow.slack.com/archives/C08P1KK6N0K/p1758284624289169?thread_ts=1758212203.399899&cid=C08P1KK6N0K
        if (!promptLabels) {
            return [];
        }
        let videoInferenceSampledData: FrameInferenceResult[] = [];

        const videoMediaItems = mediaItems.filter((mediaItem) => mediaItem.isVideo);

        if (!videoMediaItems.length) {
            return [];
        }

        // If we have videos, we will use the first one to sample inference data from. That's completely arbitrary.
        const firstVideoMediaItem = videoMediaItems[0];
        const { serverMediaDimensions } = firstVideoMediaItem.metadata || {};

        if (!serverMediaDimensions) return [];

        const videoPredictions = allPredictionsAcrossFiles
            .filter(
                (p) =>
                    !p.draft &&
                    p.fileId === firstVideoMediaItem.id &&
                    p.confidence >= (firstVideoMediaItem.confidenceThreshold || 0)
            )
            .sort((a, b) => a.frameSegment - b.frameSegment);

        const framesPredicted = new Set(videoPredictions.map((p) => p.frameSegment));

        const numOfFramesToSample = Math.min(framesPredicted.size, MIN_INFERENCES_SAMPLE);

        // We'll sample 10 equally spaced frames/inferences from the video.
        const indexStep = Math.floor((framesPredicted.size - 1) / (numOfFramesToSample - 1));

        for (let frameI = 0; frameI < framesPredicted.size - 1; frameI += indexStep) {
            const frameSegment = frameI;
            const segmentPredictions = videoPredictions.filter(
                (p) => p.frameSegment === frameSegment
            );

            const frameInferenceResult: FrameInferenceResult = {
                frame_number: frameSegment,
                file_uuid: `${workspaceUrl}/${firstVideoMediaItem.id}`,
                predictions: segmentPredictions.map((p) =>
                    omit(p, ["thumbnailDataUrl", "label", "draft", "fileId", "frameSegment"])
                ),
                image_width: serverMediaDimensions.width,
                image_height: serverMediaDimensions.height,
                file_name: firstVideoMediaItem.name,
                file_type: firstVideoMediaItem.fileType,
                fps: fps
            };
            videoInferenceSampledData.push(frameInferenceResult);
        }

        return videoInferenceSampledData;
    }, [promptLabels, mediaItems, allPredictionsAcrossFiles, workspaceUrl, fps]);

    const currentInferenceSampledData = useMemo(() => {
        // Inference sampled data is a subset of the predictions across files. We need to use inference results to reach
        // the minimum amount of data (currently 10 images) to trigger RF-DETR training.

        // If we have enough data, return it
        if (currentImageInferenceSampledData.length >= MIN_INFERENCES_SAMPLE) {
            return currentImageInferenceSampledData;
        }

        return [...currentImageInferenceSampledData, ...currentVideoInferenceSampledData];
    }, [currentImageInferenceSampledData, currentVideoInferenceSampledData]);

    const imagesNeededToDeploy = useMemo(() => {
        const promptedFiles = currentPromptingData.map((promptData) => `${promptData.file_uuid}-0`); // 0 hardcoded here because prompts are always relative to images, and images always have frameSegment/frame_number === 0
        const inferredFiles = currentInferenceSampledData.map(
            (inferenceData) => `${inferenceData.file_uuid}-${inferenceData.frame_number}`
        );

        const allFiles = new Set([...promptedFiles, ...inferredFiles]);

        return MIN_INFERENCES_SAMPLE - allFiles.size;
    }, [currentPromptingData, currentInferenceSampledData]);

    const hasEnoughDataToDeploy = useMemo(() => {
        // Text prompting is always enabled, so we always have enough data
        return true;
    }, []);

    // === memo computations ===
    // When a video is "flattened" (annotations view), currentMedia.file is set to
    // the frame, but annotations are stored under [videoId][frameId]. Use the
    // original video ID for the file-level key when flattened.
    const effectiveFileId = flattenedVideoSourceId || currentMedia?.file.id;

    const allCurrentFrameAnnotations = useMemo(() => {
        const frameId = currentMedia?.frame?.id ?? currentMedia?.file.id;
        if (!frameId || !effectiveFileId) return [];

        return getFrameAnnotations(annotationsForFileAndFrameId, effectiveFileId, frameId);
    }, [
        annotationsForFileAndFrameId,
        effectiveFileId,
        currentMedia?.file.id,
        currentMedia?.frame?.id
    ]);

    const currentFrameAnnotations = useMemo(() => {
        return allCurrentFrameAnnotations.filter((a) => !a.draft);
    }, [allCurrentFrameAnnotations]);

    // User annotations as they were before inference overwrote the annotations store.
    // Falls back to live non-draft annotations when no snapshot exists (i.e. inference hasn't run yet).
    // Also merges in any annotations drawn AFTER the snapshot was taken (user adding boxes
    // in the Review "My Annotations" view). User-drawn annotations don't carry a `confidence`
    // field, while prediction-converted ones always do — that's how we distinguish them.
    const currentFrameUserAnnotations = useMemo(() => {
        const frameId = currentMedia?.frame?.id ?? currentMedia?.file.id;
        if (!frameId || !effectiveFileId) return [];

        const snapshotAnnotations = getFrameAnnotations(
            userAnnotationsSnapshot,
            effectiveFileId,
            frameId
        );
        if (snapshotAnnotations && snapshotAnnotations.length > 0) {
            const snapshotIds = new Set(snapshotAnnotations.map((a) => a.id));
            const newlyDrawn = allCurrentFrameAnnotations.filter(
                (a) => !snapshotIds.has(a.id) && !a.confidence && !a.draft
            );
            return [...snapshotAnnotations, ...newlyDrawn];
        }

        return allCurrentFrameAnnotations.filter((a) => !a.draft);
    }, [
        effectiveFileId,
        currentMedia?.frame?.id,
        currentMedia?.file.id,
        userAnnotationsSnapshot,
        allCurrentFrameAnnotations
    ]);

    const currentFramePredictions = useMemo(() => {
        // For images, frameSegment is undefined, treat as 0. For videos, it's the segment number.
        const frameSegment = currentMedia?.frameSegment ?? 0;

        const predictions =
            predictionsForFileAndFrameSegment[currentMedia?.file.id]?.[frameSegment] || [];

        // In DEPLOY/SHARE flows, workflow inference returns predictions with workflow classes
        // Don't filter by promptLabels since we're not building a model
        if (flow === Flows.DEPLOY || flow === Flows.SHARE) {
            return predictions;
        }

        // In BUILD flow, filter by promptLabels
        const filtered = predictions.filter((p) => {
            const promptLabel = promptLabels.get(p.class);
            if (!promptLabel) return false;
            return true;
        });

        return filtered;
    }, [
        predictionsForFileAndFrameSegment,
        currentMedia?.file.id,
        currentMedia?.frameSegment,
        promptLabels,
        flow
    ]);

    const manageVideoCurrentFrameStorage = useCallback(() => {
        if (!currentMedia?.file.isVideo) return;
        if (!currentMedia?.frame) return;

        const isUploaded = [MediaItemStatus.COMPLETED, MediaItemStatus.UPLOADING].includes(
            currentMedia?.frame?.status
        );

        if (currentMedia?.frame && currentFrameAnnotations.length > 0 && !isUploaded) {
            // upload frame to storage
            uploadMediaItem(currentMedia.frame, { storageUploadDir });
        }
    }, [
        currentMedia?.frame,
        currentMedia?.file.isVideo,
        storageUploadDir,
        currentFrameAnnotations,
        uploadMediaItem
    ]);

    useEffect(() => {
        if (currentMedia?.file.isVideo && currentMedia?.frame) {
            manageVideoCurrentFrameStorage();
        }
    }, [currentMedia?.file.isVideo, currentMedia?.frame, manageVideoCurrentFrameStorage]);

    const inferredDimensions = useMemo(() => {
        if (currentMedia?.file.isVideo) {
            return currentMediaFrameInferredDimensions || currentMediaFileInferredDimensions;
        }

        return currentMediaFileInferredDimensions;
    }, [
        currentMedia?.file.isVideo,
        currentMediaFrameInferredDimensions,
        currentMediaFileInferredDimensions
    ]);

    const selectedObject = useMemo(() => {
        if (!selectedAnnotationId) return null;
        const anno = currentFrameAnnotations.find((a) => a.id === selectedAnnotationId);
        return anno?.label || null;
    }, [selectedAnnotationId, currentFrameAnnotations]);

    const value = useMemo<ComputedValues>(
        () => ({
            allCurrentFrameAnnotations,
            currentFrameAnnotations,
            currentFrameUserAnnotations,
            currentFramePredictions,
            inferredDimensions,
            currentPromptingData,
            currentInferenceSampledData,
            selectedObject,
            allAnnotationsAcrossFiles,
            allPredictionsAcrossFiles,
            imagesNeededToDeploy,
            hasEnoughDataToDeploy
        }),
        [
            allCurrentFrameAnnotations,
            currentFrameAnnotations,
            currentFrameUserAnnotations,
            currentFramePredictions,
            inferredDimensions,
            currentPromptingData,
            currentInferenceSampledData,
            selectedObject,
            allAnnotationsAcrossFiles,
            allPredictionsAcrossFiles,
            imagesNeededToDeploy,
            hasEnoughDataToDeploy
        ]
    );

    return (
        <ComputedValuesContext.Provider value={value}>{children}</ComputedValuesContext.Provider>
    );
};
